import { useCallback, useEffect, useRef, useState } from 'react'
import { classifyFailure } from '../model/apiFailure'
import { closeNowModeOf, isVigent } from '../model/closeNowMode'

export const PREVIEW_WAIT_MS = 800
export const RETRY_WAIT_MS = 1000

const INITIAL = { phase: 'idle', result: {}, resultKey: '', failure: {}, failedKey: '' }

/**
 * The rehearsal of the correction (RN56-RN60, RH-24): it asks by itself 800 ms after the LAST change of the intent, never two at
 * a time, discards the answer of an intent that is no longer the current one, retries a RETRY once after 1 s, and only aborts
 * when the panel closes (the AbortController is never used to replace a request in flight).
 *
 *   phase: idle | waiting (timer running, request in flight or retry pending) | ready | failed
 *
 * @param {object} options
 *   active        there is a panel open (false = nothing runs)
 *   sessionKey    identifies the panel; a new one starts from scratch
 *   intent        the intent to rehearse (only read when `canPreview`)
 *   intentKey     canonical text of the intent (RN60)
 *   canPreview    RN56: a change, no local error (an open intervention too, RN23)
 *   open          the reading says the intervention is open: the mode of the panel until the first preview (RN22)
 *   previewFn     (intent, signal) => Promise of the 200 body (resolves undefined when Api_Functions already redirected)
 *   describeFailure  (failure) => UiMessage of a rejection
 */
export const usePreview = ({ active, sessionKey, intent, intentKey, canPreview, open = false, previewFn, describeFailure }) => {
  const [state, setState] = useState(INITIAL)
  const [immediateToken, setImmediateToken] = useState(0)
  const stateRef = useRef(state)
  stateRef.current = state
  const latest = useRef({})
  latest.current = { intent, intentKey, canPreview, previewFn, describeFailure }
  const aliveRef = useRef(false)
  const controllerRef = useRef(undefined)
  const timerRef = useRef(0)
  const retryTimerRef = useRef(0)
  const inFlightKeyRef = useRef('')
  const queuedRef = useRef(false)
  const immediateRef = useRef(false)

  const clearTimers = useCallback(() => {
    clearTimeout(timerRef.current)
    clearTimeout(retryTimerRef.current)
    timerRef.current = 0
    retryTimerRef.current = 0
  }, [])

  // One panel = one life: everything starts again with a new key and is stopped (and aborted) when it ends.
  useEffect(() => {
    if (!active) return undefined
    aliveRef.current = true
    controllerRef.current = new AbortController()
    inFlightKeyRef.current = ''
    queuedRef.current = false
    immediateRef.current = false
    stateRef.current = INITIAL
    setState(INITIAL)
    return () => {
      aliveRef.current = false
      clearTimers()
      controllerRef.current.abort()
    }
  }, [active, sessionKey, clearTimers])

  const send = useCallback((intentToSend, key, allowRetry) => {
    inFlightKeyRef.current = key
    setState((current) => ({ ...current, phase: 'waiting' }))
    const stale = () => key !== latest.current.intentKey || !latest.current.canPreview
    // The answer of an intent that is no longer the current one is dropped; if a change waited for the flight to end, it goes now.
    const dropAndContinue = () => {
      if (queuedRef.current && latest.current.canPreview) {
        queuedRef.current = false
        send(latest.current.intent, latest.current.intentKey, true)
      } else {
        setState((current) => (timerRef.current ? current : { ...current, phase: current.phase === 'waiting' ? 'idle' : current.phase }))
      }
    }
    latest.current.previewFn(intentToSend, controllerRef.current.signal).then((result) => {
      inFlightKeyRef.current = ''
      if (!aliveRef.current) return
      if (result === undefined) { // Api_Functions already redirected to /connection_error: nothing more to do (RN74)
        setState((current) => ({ ...current, phase: 'idle' }))
        return
      }
      if (stale()) { dropAndContinue(); return }
      setState({ phase: 'ready', result, resultKey: key, failure: {}, failedKey: '' })
    }, (error) => {
      inFlightKeyRef.current = ''
      if (!aliveRef.current) return
      const failure = classifyFailure(error)
      if (failure.kind === 'aborted') return
      if (stale()) { dropAndContinue(); return }
      if (failure.kind === 'contract' && failure.message === 'DATA_CORRECTION_RETRY' && allowRetry) {
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = 0
          if (aliveRef.current && key === latest.current.intentKey) send(latest.current.intent, key, false)
        }, RETRY_WAIT_MS)
        return
      }
      setState({ phase: 'failed', result: {}, resultKey: '', failure: latest.current.describeFailure(failure), failedKey: key })
    })
  }, [])

  // Waiting for the user to stop touching the intent, then asking (RN56).
  useEffect(() => {
    if (!active || !canPreview) {
      setState((current) => (current.phase === 'ready' || current.phase === 'idle' ? current : { ...current, phase: 'idle' }))
      return undefined
    }
    const current = stateRef.current
    if (current.phase === 'ready' && current.resultKey === intentKey) return undefined
    if (inFlightKeyRef.current === intentKey) return undefined
    if (current.phase === 'failed' && current.failedKey === intentKey) return undefined
    // «At once» (adopted tapes, only the presence, the plan changed) asks right away; if one is in flight it goes when it ends.
    if (immediateRef.current) {
      immediateRef.current = false
      if (inFlightKeyRef.current) queuedRef.current = true
      else send(latest.current.intent, latest.current.intentKey, true)
      return clearTimers
    }
    setState((existing) => ({ ...existing, phase: 'waiting' }))
    timerRef.current = setTimeout(() => {
      timerRef.current = 0
      if (inFlightKeyRef.current) { queuedRef.current = true; return }
      send(latest.current.intent, latest.current.intentKey, true)
    }, PREVIEW_WAIT_MS)
    return clearTimers
  }, [active, sessionKey, intentKey, canPreview, immediateToken, send, clearTimers])

  /** Asks again without waiting (after adopting tapes, «Correct only the presence», PLAN_CHANGED). */
  const requestImmediate = useCallback(() => {
    immediateRef.current = true
    setImmediateToken((token) => token + 1)
  }, [])

  /** The preview is no longer valid (the apply was rejected by the engine, or Sun said the plan changed). */
  const invalidate = useCallback(() => setState((current) => ({ ...INITIAL, phase: current.phase === 'waiting' ? 'waiting' : 'idle' })), [])

  /** «Try again» (G13): a new attempt that does not retry by itself. */
  const tryAgain = useCallback(() => send(latest.current.intent, latest.current.intentKey, false), [send])

  // RN22-RN23: the mode is the one of the latest preview (the reading's before any) and the vigency depends on it.
  const closeNowMode = closeNowModeOf({ open }, state.result)
  return {
    phase: state.phase,
    result: state.result,
    failure: state.failure,
    waiting: state.phase === 'waiting',
    closeNowMode,
    vigent: isVigent(state, intentKey, closeNowMode),
    requestImmediate,
    invalidate,
    tryAgain
  }
}
